// modules/settings/ui.ts — <app-panel-config>

import { bus }       from '../../core/event-bus';
import { getModule } from '../../core/registry';
import { useBus }    from '../../shared/hooks/use-bus';
import { getThemePref, setThemePref, type ThemePref } from '../../core/theme';

export class AppPanelConfig extends HTMLElement {
  static define() { customElements.define('app-panel-config', AppPanelConfig); }

  connectedCallback() {
    this.render();
    useBus('auth:signed-in', ({ user }) => this.populateUser(user as { email: string; nickname: string | null; sheetId: string | null; hasGeminiKey: boolean }), this);
    useBus('settings:updated', () => this.refreshVoices(), this);
  }

  private render() {
    this.innerHTML = `
      <style>
        h2    { font-family:var(--font-display);font-size:var(--text-xl);margin-bottom:var(--space-4); }
        .card { background:var(--color-surface);border:1px solid var(--color-border);
                border-radius:var(--radius-lg);padding:var(--space-4);margin-bottom:var(--space-3); }
        h3    { font-size:var(--text-base);font-weight:var(--font-weight-semibold);margin-bottom:var(--space-3); }
        label { font-size:var(--text-xs);color:var(--color-text-muted);display:block;margin-bottom:var(--space-1); }
        input, select {
          width:100%;background:var(--color-bg);border:1px solid var(--color-border);
          border-radius:var(--radius-md);padding:var(--space-2) var(--space-3);
          color:var(--color-text);font-family:var(--font-body);font-size:var(--text-sm);
        }
        .row  { display:flex;gap:var(--space-2);align-items:center;margin-bottom:var(--space-2); }
        .row input { margin-bottom:0; }
        .btn  { background:var(--color-primary);color:white;border:none;border-radius:var(--radius-md);
                padding:var(--space-2) var(--space-4);font-family:var(--font-body);
                font-size:var(--text-sm);font-weight:var(--font-weight-semibold);cursor:pointer;white-space:nowrap; }
        .btn-ghost { background:var(--color-surface-raised);color:var(--color-text-muted);border:1px solid var(--color-border); }
        .btn-danger { background:var(--color-error); }
        .user-email { font-size:var(--text-sm);color:var(--color-primary);font-weight:var(--font-weight-semibold);margin-bottom:var(--space-3); }
        .link { color:var(--color-primary);font-size:var(--text-xs);display:inline-block;margin-top:var(--space-2); }
        .msg  { font-size:var(--text-xs);min-height:1.2em;margin-top:var(--space-1); }
        .toggle-row { display:flex;align-items:center;gap:var(--space-3); }
        .toggle { position:relative;width:36px;height:20px;cursor:pointer; }
        .toggle input { opacity:0;width:0;height:0; }
        .slider { position:absolute;inset:0;background:var(--color-border);border-radius:20px;transition:.2s; }
        .slider:before { content:'';position:absolute;width:16px;height:16px;border-radius:50%;background:var(--color-primary-text);left:2px;top:2px;transition:.2s; }
        .toggle input:checked + .slider { background:var(--color-primary); }
        .toggle input:checked + .slider:before { left:18px; }
      </style>

      <h2>Configuración</h2>

      <!-- Cuenta -->
      <div class="card">
        <h3>👤 Tu cuenta</h3>
        <p class="user-email" id="cfgEmail">Cargando…</p>
        <div class="row">
          <input type="text" id="cfgNickname" placeholder="Tu nombre">
          <button class="btn" id="btnNickname">Guardar</button>
        </div>
        <p class="msg" id="nicknameMsg"></p>
        <button class="btn btn-ghost" id="btnSignOut" style="width:100%;margin-top:var(--space-2)">Cerrar sesión</button>
      </div>

      <!-- Gemini Key -->
      <div class="card">
        <h3>🤖 API Key de Gemini</h3>
        <p style="font-size:var(--text-xs);color:var(--color-text-muted);margin-bottom:var(--space-2)">
          Tu key se encripta y guarda de forma segura. Obtenela gratis en AI Studio.
        </p>
        <form class="row" id="geminiKeyForm" autocomplete="off">
          <input type="password" id="cfgGeminiKey" placeholder="AIzaSy…" autocomplete="off">
          <button class="btn" type="submit" id="btnSaveKey">Guardar</button>
        </form>
        <a class="link" href="https://aistudio.google.com/apikey" target="_blank" rel="noopener">🔑 Obtener key gratis</a>
        <p class="msg" id="keyMsg"></p>
      </div>

      <!-- Hoja de cálculo -->
      <div class="card">
        <h3>📊 Tu hoja de cálculo</h3>
        <p style="font-size:var(--text-xs);color:var(--color-text-muted);margin-bottom:var(--space-2)">
          Tus datos viven en tu Google Drive.
        </p>
        <a class="link" id="cfgSheetLink" href="#" target="_blank">Abrir mi hoja ↗</a>
        <div style="margin-top:var(--space-3)">
          <label>Vincular hoja existente (ID o URL)</label>
          <div class="row">
            <input type="text" id="cfgSheetId" placeholder="ID de la hoja o URL completa">
            <button class="btn" id="btnLinkSheet">Vincular</button>
          </div>
        </div>
      </div>

      <!-- Apariencia -->
      <div class="card">
        <h3>🎨 Apariencia</h3>
        <label>Tema</label>
        <select id="themeSel">
          <option value="system">Igual que el dispositivo</option>
          <option value="light">Claro</option>
          <option value="dark">Oscuro</option>
        </select>
      </div>

      <!-- Voz -->
      <div class="card">
        <h3>🔊 Respuesta de voz</h3>
        <div class="toggle-row" style="margin-bottom:var(--space-3)">
          <label class="toggle">
            <input type="checkbox" id="audioToggle" checked>
            <span class="slider"></span>
          </label>
          <span style="font-size:var(--text-sm)">Respuesta en audio</span>
        </div>
        <label>Voz del asistente</label>
        <div style="display:flex;gap:var(--space-2)">
          <select id="voiceSel" style="flex:1;min-width:0"></select>
          <button class="btn btn-ghost" id="btnTestVoice" type="button" title="Probar voz">▶ Probar</button>
        </div>
      </div>
    `;

    this.bindEvents();
    this.refreshVoices();
  }

  private populateUser(user: { email: string; nickname: string | null; sheetId: string | null; hasGeminiKey: boolean }) {
    const emailEl = this.querySelector<HTMLElement>('#cfgEmail')!;
    if (emailEl) emailEl.textContent = user.email;

    const nicknameEl = this.querySelector<HTMLInputElement>('#cfgNickname')!;
    if (nicknameEl && user.nickname) nicknameEl.value = user.nickname;

    const sheetLink = this.querySelector<HTMLAnchorElement>('#cfgSheetLink')!;
    if (sheetLink && user.sheetId) {
      sheetLink.href = `https://docs.google.com/spreadsheets/d/${user.sheetId}`;
    }

    const keyMsg = this.querySelector<HTMLElement>('#keyMsg')!;
    if (keyMsg) {
      keyMsg.textContent = user.hasGeminiKey ? '✓ API key configurada' : '';
      keyMsg.style.color = 'var(--color-success)';
    }
  }

  private bindEvents() {
    // Nickname
    this.querySelector('#btnNickname')?.addEventListener('click', async () => {
      const val = (this.querySelector('#cfgNickname') as HTMLInputElement).value.trim();
      if (!val) return;
      const { supabase } = await import('../../services/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await supabase.from('users').update({ nickname: val }).eq('id', session.user.id);
      const msg = this.querySelector<HTMLElement>('#nicknameMsg')!;
      msg.textContent   = '✓ Nombre actualizado';
      msg.style.color   = 'var(--color-success)';
    });

    // Gemini key
    this.querySelector('#geminiKeyForm')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const val  = (this.querySelector('#cfgGeminiKey') as HTMLInputElement).value.trim();
      const msg  = this.querySelector<HTMLElement>('#keyMsg')!;
      const btn  = this.querySelector<HTMLButtonElement>('#btnSaveKey')!;
      if (!val) return;
      btn.disabled = true;
      try {
        await getModule<{ saveGeminiKey: (k: string) => Promise<void> }>('settings')?.saveGeminiKey(val);
        msg.textContent = '✓ Key guardada y encriptada';
        msg.style.color = 'var(--color-success)';
        (this.querySelector('#cfgGeminiKey') as HTMLInputElement).value = '';
      } catch (err) {
        msg.textContent = err instanceof Error ? err.message : 'Error';
        msg.style.color = 'var(--color-error)';
      }
      btn.disabled = false;
    });

    // Link sheet
    this.querySelector('#btnLinkSheet')?.addEventListener('click', async () => {
      let val = (this.querySelector('#cfgSheetId') as HTMLInputElement).value.trim();
      if (!val) return;
      // Extraer ID si es URL completa
      const match = val.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
      if (match) val = match[1];
      await getModule<{ saveSheetId: (id: string) => Promise<void> }>('settings')?.saveSheetId(val);
      bus.emit('notifications:show', { message: 'Hoja vinculada. Importando datos…', type: 'info' });
      getModule<{ importFromSheetId: (id: string) => Promise<number> }>('import')?.importFromSheetId(val);
    });

    // Sign out
    this.querySelector('#btnSignOut')?.addEventListener('click', () => {
      getModule<{ signOut: () => Promise<void> }>('auth')?.signOut();
    });

    // Audio toggle
    const toggle = this.querySelector<HTMLInputElement>('#audioToggle')!;
    const saved  = localStorage.getItem('platik:audioEnabled');
    if (saved !== null) toggle.checked = JSON.parse(saved);
    toggle.addEventListener('change', () => {
      localStorage.setItem('platik:audioEnabled', String(toggle.checked));
    });

    // Probar la voz seleccionada (suena siempre, sin importar el toggle de audio)
    this.querySelector('#btnTestVoice')?.addEventListener('click', () => this.previewVoice());

    // Tema claro / oscuro / sistema
    const themeSel = this.querySelector<HTMLSelectElement>('#themeSel');
    if (themeSel) {
      themeSel.value = getThemePref();
      themeSel.addEventListener('change', () => setThemePref(themeSel.value as ThemePref));
    }
  }

  private previewVoice() {
    if (!window.speechSynthesis) return;
    const sel   = this.querySelector<HTMLSelectElement>('#voiceSel');
    const synth = window.speechSynthesis;
    synth.cancel();
    const u = new SpeechSynthesisUtterance('Hola, soy tu asistente de finanzas. Así voy a sonar.');
    const voice = sel ? synth.getVoices().find(v => v.name === sel.value) : undefined;
    if (voice) { u.voice = voice; u.lang = voice.lang; }
    synth.speak(u);
  }

  private refreshVoices() {
    const sel = this.querySelector<HTMLSelectElement>('#voiceSel');
    if (!sel || !window.speechSynthesis) return;

    const populate = () => {
      const voices = window.speechSynthesis.getVoices();
      if (!voices.length) return;   // aún no cargaron (carga async)

      sel.innerHTML = voices
        .map(v => `<option value="${v.name}">${v.name} (${v.lang})</option>`)
        .join('');

      const saved     = localStorage.getItem('platik:voiceName');
      const devLang   = navigator.language || 'es-AR';        // p. ej. "es-AR"
      const devPrefix = devLang.split('-')[0];                // "es"
      const pick =
        (saved && voices.find(v => v.name === saved)) ||
        voices.find(v => v.lang.replace('_', '-') === devLang) ||
        voices.find(v => v.lang.replace('_', '-').startsWith(devPrefix)) ||
        voices[0];
      if (pick) sel.value = pick.name;
    };

    populate();
    // getVoices() suele venir vacío en la 1ª llamada → repoblar al cargar las voces.
    window.speechSynthesis.onvoiceschanged = populate;

    if (!sel.dataset.bound) {
      sel.dataset.bound = '1';
      sel.addEventListener('change', () => localStorage.setItem('platik:voiceName', sel.value));
    }
  }
}
